import React from 'react';
import HelpBadge from './components/HelpBadge';
import {
  OFAC_OTHER_VALUE,
  ofacAddressLayout,
  ofacAdminLabels,
  ofacCityBeforeCounty,
  ofacCityHelp,
  ofacCityOptions,
  ofacCountryOptions,
  ofacCountyFromCity,
  ofacCountyOptions,
  ofacStateOptions,
  resolvedOfacPart,
  withOtherOption,
  type OfacLanguage,
} from './ofacIntakeCatalog';

export type OfacLocationValue = {
  country?: string;
  otherCountry?: string;
  region?: string;
  otherRegion?: string;
  county?: string;
  otherCounty?: string;
  city?: string;
  otherCity?: string;
};

type OfacLocationSelectProps = {
  language: OfacLanguage;
  includeCountry?: boolean;
  followLayout?: boolean;
  value: OfacLocationValue;
  onChange: (next: OfacLocationValue) => void;
  disabled?: boolean;
};

const GEO_FIELDS = new Set(['country', 'region', 'county', 'city']);

export function OfacLocationSelect({
  language,
  includeCountry = true,
  followLayout = false,
  value,
  onChange,
  disabled = false,
}: OfacLocationSelectProps) {
  const country = String(value.country || '');
  const layout = ofacAddressLayout(country);
  const showRegion = !followLayout || layout.includes('region');
  const showCounty = !followLayout || layout.includes('county');
  const showCity = !followLayout || layout.includes('city');
  const cityBeforeCounty = ofacCityBeforeCounty(country);
  const labels = ofacAdminLabels(country, language);
  const countryOptions = withOtherOption(ofacCountryOptions(language), language);
  const stateOptions = withOtherOption(ofacStateOptions(country), language);
  const countyOptions = withOtherOption(ofacCountyOptions(country, value.region || ''), language);
  const cityOptions = withOtherOption(
    ofacCityOptions(country, value.region, value.county),
    language,
  );
  const countyOverriddenRef = React.useRef(false);
  const lastCityKeyRef = React.useRef('');

  const patch = (next: OfacLocationValue) => {
    onChange({ ...value, ...next });
  };

  const otherLabel = language === 'es' ? 'Especificar' : 'Type it';
  const cityKeyFor = (city: string, otherCity: string, region = value.region || '') => (
    `${country}|${region}|${resolvedOfacPart(city, otherCity).trim().toLowerCase()}`
  );

  const suggestedCountyPatch = (
    nextCity: string,
    nextOtherCity: string,
    region = value.region || '',
  ): Pick<OfacLocationValue, 'county' | 'otherCounty'> => {
    const cityKey = cityKeyFor(nextCity, nextOtherCity, region);
    if (cityKey !== lastCityKeyRef.current) {
      lastCityKeyRef.current = cityKey;
      countyOverriddenRef.current = false;
    }
    const suggested = ofacCountyFromCity(
      country,
      region,
      resolvedOfacPart(nextCity, nextOtherCity),
    );
    if (!suggested || countyOverriddenRef.current || suggested === value.county) {
      return {};
    }
    return { county: suggested, otherCounty: '' };
  };

  const countyFields = showCounty ? (
    <>
      <label>
        <span>{labels.county}</span>
        <select
          value={value.county || ''}
          disabled={disabled}
          onChange={(event) => {
            countyOverriddenRef.current = true;
            patch({
              county: event.target.value,
              otherCounty: event.target.value === OFAC_OTHER_VALUE ? value.otherCounty : '',
            });
          }}
        >
          <option value="">{language === 'es' ? 'Elegir' : 'Select'}</option>
          {countyOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      {value.county === OFAC_OTHER_VALUE ? (
        <label>
          <span>{otherLabel}</span>
          <input
            type="text"
            disabled={disabled}
            value={value.otherCounty || ''}
            onChange={(event) => {
              countyOverriddenRef.current = true;
              patch({ otherCounty: event.target.value });
            }}
          />
        </label>
      ) : null}
    </>
  ) : null;

  const cityFields = showCity ? (
    <>
      <div className="dashboard-ofac-geo-field">
        <span className="help-badge-label-row">
          {labels.city}
          <HelpBadge label={language === 'es' ? 'Ayuda sobre la ciudad' : 'Help about city'}>
            <p>{ofacCityHelp(country, language)}</p>
          </HelpBadge>
        </span>
        <select
          aria-label={labels.city}
          value={value.city || ''}
          disabled={disabled}
          onChange={(event) => {
            const city = event.target.value;
            const otherCity = city === OFAC_OTHER_VALUE ? value.otherCity : '';
            patch({
              city,
              otherCity,
              ...suggestedCountyPatch(city, otherCity),
            });
          }}
        >
          <option value="">{language === 'es' ? 'Elegir' : 'Select'}</option>
          {cityOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>
      {value.city === OFAC_OTHER_VALUE ? (
        <label>
          <span>{otherLabel}</span>
          <input
            type="text"
            disabled={disabled}
            value={value.otherCity || ''}
            onChange={(event) => {
              const otherCity = event.target.value;
              patch({
                otherCity,
                ...suggestedCountyPatch(value.city || '', otherCity),
              });
            }}
          />
        </label>
      ) : null}
    </>
  ) : null;

  return (
    <div className="dashboard-form-grid dashboard-ofac-whitelist-intake__span">
      {includeCountry ? (
        <>
          <label>
            <span>{language === 'es' ? 'País' : 'Country'}</span>
            <select
              value={country}
              disabled={disabled}
              onChange={(event) => {
                countyOverriddenRef.current = false;
                lastCityKeyRef.current = '';
                patch({
                  country: event.target.value,
                  otherCountry: event.target.value === OFAC_OTHER_VALUE ? value.otherCountry : '',
                  region: '',
                  otherRegion: '',
                  county: '',
                  otherCounty: '',
                  city: '',
                  otherCity: '',
                });
              }}
            >
              <option value="">{language === 'es' ? 'Elegir país' : 'Select country'}</option>
              {countryOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          {country === OFAC_OTHER_VALUE ? (
            <label>
              <span>{otherLabel}</span>
              <input
                type="text"
                disabled={disabled}
                value={value.otherCountry || ''}
                onChange={(event) => patch({ otherCountry: event.target.value })}
              />
            </label>
          ) : null}
        </>
      ) : null}
      {showRegion ? (
        <>
          <label>
            <span>{labels.region}</span>
            <select
              value={value.region || ''}
              disabled={disabled}
              onChange={(event) => {
                countyOverriddenRef.current = false;
                lastCityKeyRef.current = '';
                patch({
                  region: event.target.value,
                  otherRegion: event.target.value === OFAC_OTHER_VALUE ? value.otherRegion : '',
                  county: '',
                  otherCounty: '',
                  city: '',
                  otherCity: '',
                });
              }}
            >
              <option value="">{language === 'es' ? 'Elegir' : 'Select'}</option>
              {stateOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          {value.region === OFAC_OTHER_VALUE ? (
            <label>
              <span>{otherLabel}</span>
              <input
                type="text"
                disabled={disabled}
                value={value.otherRegion || ''}
                onChange={(event) => patch({ otherRegion: event.target.value })}
              />
            </label>
          ) : null}
        </>
      ) : null}
      {cityBeforeCounty ? cityFields : countyFields}
      {cityBeforeCounty ? countyFields : cityFields}
    </div>
  );
}

export function ofacLocationUsesGeoField(field: string): boolean {
  return GEO_FIELDS.has(field);
}
